import type {
  DatePartTextFormat,
  DateDifferencePart,
} from "../blueprint/types.js";
import { resolvePath } from "../layout/resolution.js";
import { TransitionConfig } from "../layout/types.js";
import type { TextTooltipDefinition } from "../layout/types.js";
import type { RenderTooltip, RuntimeContext } from "./types.js";

export function coerceToBoolean(raw: unknown): boolean {
  if (raw === true || raw === "true" || raw === 1) return true;
  if (raw === false || raw === "false" || raw === 0) return false;
  if (typeof raw === "string") return raw.length > 0;
  return raw != null;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const DEFAULT_BORDER_SIDE = "1px solid #000000";

export function toBorderConfig(value: unknown) {
  const side = String(value ?? "").trim() || DEFAULT_BORDER_SIDE;
  return {
    border: {
      top: side,
      right: side,
      bottom: side,
      left: side,
    },
  };
}

const DEFAULT_BORDER_RADIUS = "0px";

export function toBorderRadiusConfig(value: unknown) {
  const radius = String(value ?? "").trim() || DEFAULT_BORDER_RADIUS;
  return {
    radius: {
      topLeft: radius,
      topRight: radius,
      bottomRight: radius,
      bottomLeft: radius,
    },
  };
}

export function toObjectValue(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed != null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

export function toListValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function evaluateObjectDefined(value: unknown): boolean {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

export function evaluateObjectEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return true;
  return Object.keys(value as Record<string, unknown>).length === 0;
}

export const normalizeColorString = (raw: unknown): string =>
  String(raw ?? "")
    .trim()
    .toLowerCase();

export const toColorMixRatio = (raw: unknown): number => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 50;
  return clamp(n, 0, 100);
};

const isCssPropertyValueSupported = (
  property: "filter" | "box-shadow" | "text-shadow" | "background-image",
  value: string,
): boolean => {
  const candidate = value.trim();
  if (!candidate) return false;

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports(property, candidate);
  }

  if (typeof document !== "undefined") {
    const probe = document.createElement("span");
    probe.style.setProperty(property, "");
    probe.style.setProperty(property, candidate);
    return probe.style.getPropertyValue(property) !== "";
  }

  return candidate === "none" || candidate.length > 0;
};

export const isColorString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) return false;

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", candidate);
  }

  if (typeof document !== "undefined") {
    const probe = document.createElement("span");
    probe.style.color = "";
    probe.style.color = candidate;
    return probe.style.color !== "";
  }

  const hexColorRegex = /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i;
  const rgbColorRegex =
    /^rgba?\(\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*,\s*\d{1,3}%?(?:\s*,\s*(?:\d*\.?\d+%?))?\s*\)$/i;
  const hslColorRegex =
    /^hsla?\(\s*-?\d*\.?\d+(?:deg|grad|rad|turn)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(?:\s*,\s*(?:\d*\.?\d+%?))?\s*\)$/i;
  const advancedFunctionColorRegex =
    /^(?:rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch|color|light-dark)\(.+\)$/i;
  const keywordColorRegex =
    /^(?:transparent|currentcolor|inherit|initial|unset|revert|revert-layer|[a-z]+)$/i;

  return (
    hexColorRegex.test(candidate) ||
    rgbColorRegex.test(candidate) ||
    hslColorRegex.test(candidate) ||
    advancedFunctionColorRegex.test(candidate) ||
    keywordColorRegex.test(candidate)
  );
};

export const isFilterString = (value: string): boolean =>
  isCssPropertyValueSupported("filter", value);

export const isBoxShadowString = (value: string): boolean =>
  isCssPropertyValueSupported("box-shadow", value);

export const isTextShadowString = (value: string): boolean =>
  isCssPropertyValueSupported("text-shadow", value);

export const isGradientString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) return false;
  if (!/^(?:repeating-)?(?:linear|radial|conic)-gradient\(/i.test(candidate)) {
    return false;
  }
  return isCssPropertyValueSupported("background-image", candidate);
};

export const isDateString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) return false;

  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(?:[T\s].+)?$/;
  if (isoDateRegex.test(candidate)) {
    const dateOnlyMatch = candidate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, yearRaw, monthRaw, dayRaw] = dateOnlyMatch;
      const year = Number(yearRaw);
      const month = Number(monthRaw);
      const day = Number(dayRaw);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return (
        !Number.isNaN(parsed.getTime()) &&
        parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day
      );
    }
    const parsed = new Date(candidate);
    return !Number.isNaN(parsed.getTime());
  }

  const frDateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const frMatch = candidate.match(frDateRegex);
  if (frMatch) {
    const [, dayRaw, monthRaw, yearRaw] = frMatch;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
      !Number.isNaN(parsed.getTime()) &&
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  }

  return false;
};

export function listFindValuesMatch(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (typeof a === "boolean" || typeof b === "boolean") {
    return Boolean(a) === Boolean(b);
  }
  if (typeof a === "number" || typeof b === "number") {
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
  }
  const sa = typeof a === "string" ? a : String(a ?? "");
  const sb = typeof b === "string" ? b : String(b ?? "");
  if (isDateString(sa) && isDateString(sb)) {
    return new Date(sa).getTime() === new Date(sb).getTime();
  }
  return sa === sb;
}

export function evaluateListFind(
  list: unknown,
  needle: unknown,
  objectFieldPath?: string,
): unknown {
  const arr = toListValue(list);
  const path = objectFieldPath?.trim();
  return arr.find((item) => {
    const fieldValue =
      path && typeof item === "object" && item != null
        ? resolvePath(item, path)
        : item;
    return listFindValuesMatch(fieldValue, needle);
  });
}

export function evaluateListFindIndex(
  list: unknown,
  needle: unknown,
  objectFieldPath?: string,
): number {
  const arr = toListValue(list);
  const path = objectFieldPath?.trim();
  return arr.findIndex((item) => {
    const fieldValue =
      path && typeof item === "object" && item != null
        ? resolvePath(item, path)
        : item;
    return listFindValuesMatch(fieldValue, needle);
  });
}

export function evaluateListFindByPath(
  list: unknown,
  pathRaw: unknown,
  expectedRaw: unknown,
): unknown {
  const arr = toListValue(list);
  const path = String(pathRaw ?? "").trim();
  if (!path) return undefined;
  return arr.find((item) => {
    if (item == null || typeof item !== "object") return false;
    const fieldValue = resolvePath(item, path);
    return listFindValuesMatch(fieldValue, expectedRaw);
  });
}

export function evaluateListFindByPathIndex(
  list: unknown,
  pathRaw: unknown,
  expectedRaw: unknown,
): number {
  const arr = toListValue(list);
  const path = String(pathRaw ?? "").trim();
  if (!path) return -1;
  return arr.findIndex((item) => {
    if (item == null || typeof item !== "object") return false;
    const fieldValue = resolvePath(item, path);
    return listFindValuesMatch(fieldValue, expectedRaw);
  });
}

export type UnaryOperatorOptions = {
  datePartTextFormat?: DatePartTextFormat;
  input1ValueType?: string;
};

export type BinaryOperatorOptions = {
  dateDifferencePart?: DateDifferencePart;
  objectFieldPath?: string;
  outputKind?: "index" | "item";
};

/**
 * Build a CSS `transition` shorthand from node-level transition entries.
 */
export function buildCssTransition(
  transitions: TransitionConfig[] | undefined,
): string | undefined {
  if (!transitions?.length) return undefined;
  const enabledTransitions = transitions.filter(
    (transition) => transition?.enabled === true && transition.property,
  );
  if (enabledTransitions.length === 0) return undefined;
  return enabledTransitions
    .map((transition) => {
      const duration = `${(transition.durationMs ?? 300) / 1000}s`;
      const easing = transition.easing || "ease-out";
      const delay = transition.delayMs ? `${transition.delayMs / 1000}s` : "0s";
      return `${transition.property} ${duration} ${easing} ${delay}`;
    })
    .join(", ");
}

/**
 * Merges a layout node's static style with its compiled CSS `transition` shorthand.
 */
export function compileNodeStyle(
  style: Record<string, unknown> | undefined,
  transitions: TransitionConfig[] | undefined,
): Record<string, unknown> {
  const transition = buildCssTransition(transitions);
  if (!transition) {
    return { ...(style ?? {}) };
  }
  return { ...(style ?? {}), transition };
}

/**
 * Returns true when at least one enabled transition animates `top`, `left` or `all`.
 */
/**
 * Resolves a persisted primitive tooltip definition into a render-time tooltip.
 * Returns `undefined` when the tooltip is absent or empty.
 */
export function resolvePrimitiveTooltip(
  tooltip: TextTooltipDefinition | null | undefined,
  context?: Pick<RuntimeContext, "layoutListRowItem" | "rowIndex">,
): RenderTooltip | undefined {
  if (!tooltip) {
    return undefined;
  }

  if (tooltip.kind === "text") {
    const text = tooltip.text.trim();
    return text.length > 0 ? { kind: "text", text } : undefined;
  }

  if (tooltip.kind === "layout" && tooltip.layout) {
    return {
      kind: "layout",
      layout: tooltip.layout,
      backgroundColor: tooltip.backgroundColor,
      opacity: tooltip.opacity,
      ...(context && "layoutListRowItem" in context
        ? {
            rowContext: {
              layoutListRowItem: context.layoutListRowItem,
              rowIndex: context.rowIndex,
            },
          }
        : {}),
    };
  }

  return undefined;
}

export function transitionsTouchTopLeft(
  transitions: TransitionConfig[] | undefined,
): boolean {
  if (!transitions?.length) {
    return false;
  }

  return transitions.some((transition) => {
    if (!transition || transition.enabled !== true || !transition.property) {
      return false;
    }
    const property = String(transition.property).trim().toLowerCase();
    return property === "top" || property === "left" || property === "all";
  });
}
